import { forwardRef, useMemo, useState } from "react";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../sudokit/components/Dialog";
import { SudoButton } from "../sudokit/components/SudoButton";
import { SudoTooltip } from "../sudokit/components/SudoTooltip";
import { getDraftEditRecord } from "../../../src/shared/draftEditDiff";
import { buildDraftEditRows } from "./draftEditRows";

// Make the tooltip's actual reference focusable, so its aria-describedby and
// keyboard interaction belong to the highlighted phrase itself.
const DiffPhrase = forwardRef(function DiffPhrase(props, ref) {
  return <span ref={ref} tabIndex={0} {...props} />;
});

function DiffSegment({ part, side, onReasonOpenChange }) {
  if (!part.changed) return <span>{part[side]}</span>;
  if (!part[side]) return null;
  return (
    <SudoTooltip
      role="tooltip"
      onOpenChange={onReasonOpenChange}
      text={part.reasons.join("\n") || "No edit reason was saved for this change."}
      triggerAs={DiffPhrase}
      triggerClassName={`cursor-help rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 ${side === "before" ? "bg-red-100 text-red-900" : "bg-green-100 text-green-900"}`}
      tooltipClassName="max-w-sm bg-gray-900 text-left text-white whitespace-pre-line"
      arrowColor="#111827"
      position="top"
    >
      {side === "before" ? <del>{part.before}</del> : <ins className="no-underline">{part.after}</ins>}
    </SudoTooltip>
  );
}

export default function DraftEditDiffButton({ completion }) {
  const [open, setOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const record = useMemo(() => open ? getDraftEditRecord(completion) : null, [open, completion]);
  const rows = useMemo(() => buildDraftEditRows(record?.segments || []), [record]);
  if (completion?.functionName !== "draft/editPass") return null;
  return (
    <Dialog
      open={open}
      onOpenChange={value => {
        setOpen(value);
        setReasonOpen(false);
      }}
    >
      <DialogTrigger asChild>
        <SudoButton variant="secondary" size="small" aria-label="View edit pass diff">View Diff</SudoButton>
      </DialogTrigger>
      <DialogContent
        className="flex max-h-[90vh] w-[96vw] max-w-[1440px] flex-col overflow-hidden bg-white p-0 text-left text-gray-900"
        backdropColor="always-dark"
        hideClose
        onEscapeKeyDown={event => {
          // Let Escape dismiss the reason before dismissing the entire diff.
          if (reasonOpen) event.preventDefault();
        }}
      >
        <DialogClose asChild>
          <SudoButton type="button" variant="transparent" aria-label="Close diff" leftIcon="/assets/close.svg" className="absolute right-3 top-3 h-10 w-10 p-0 text-gray-600" />
        </DialogClose>
        <DialogHeader className="px-6 pb-4 pt-6 pr-16 text-left">
          <DialogTitle className="!text-gray-900">Draft edit pass</DialogTitle>
          <DialogDescription className="pt-2 text-sm text-gray-600">
            Removed text is on the left; added text is on the right. Space below shorter paragraphs keeps the drafts aligned.
            {" "}Hover or focus a highlighted phrase for Gemini’s edit reason.
          </DialogDescription>
        </DialogHeader>
        {record?.error ? <p role="status" className="p-6">{record.error}</p> : record && (
          <div className="min-h-0 overflow-auto border-t border-gray-200" aria-label="Draft comparison">
            {record.before === record.after && <p role="status" className="px-6 py-3">
              {record.skippedEdits?.length ? "No changes were applied. Unmatched or ambiguous edits were skipped." : "No changes. The draft already satisfies this edit pass."}
            </p>}
            {!!record.skippedEdits?.length && (
              <details className="border-b px-6 py-3">
                <summary className="cursor-pointer">
                  {record.skippedEdits.length} {record.skippedEdits.length === 1 ? "edit" : "edits"} skipped
                </summary>
                <ul>
                  {record.skippedEdits.map((edit, i) => <li key={i} className="py-2">Line {edit.lineNumber}: <q>{edit.textToReplace}</q> — {edit.rejectionReason}</li>)}
                </ul>
              </details>
            )}
            <div className="min-w-[640px]">
              <div className="sticky top-0 z-10 grid grid-cols-2 border-b bg-gray-50 text-sm font-semibold">
                <h3 className="px-6 py-3">Before edit pass</h3>
                <h3 className="px-6 py-3">After edit pass</h3>
              </div>
              <div className="py-5 text-sm leading-7">
                {rows.map((row, rowIndex) => (
                  <div key={rowIndex} data-diff-row={rowIndex} className="grid grid-cols-2">
                    {["before", "after"].map(side => (
                      <div key={side} data-diff-side={side} className="min-h-7 min-w-0 whitespace-pre-wrap break-words border-r border-gray-200 px-6">
                        {row.map((part, index) => <DiffSegment key={index} part={part} side={side} onReasonOpenChange={setReasonOpen} />)}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            {!!record.reviewFlags?.length && (
              <details className="border-t p-6 text-left">
                <summary className="cursor-pointer text-left">
                  {record.reviewFlags.length} {record.reviewFlags.length === 1 ? "item" : "items"} for human review
                </summary>
                <ul>
                  {record.reviewFlags.map((flag, i) => <li key={i} className="py-2"><q>{flag.text}</q> — {flag.reason}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
